import React, { useState, useEffect } from "react";
import AuthService from "./services/auth.service";
import { useAppContext } from "./AppContext";
import { ToastContainer, toast, Bounce } from 'react-toastify/unstyled';
import moment from "moment";
import 'moment/locale/nl'; 
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

import { LoggedInRoutes, LoggedOutRoutes, shouldShowParticles } from './routing/AppRoutes';

import ParticlesComponent from "./login/ParticlesComponent";
import ModalManager from "./modals/ModalManager";
import WelcomeWizard from "./onboarding/WelcomeWizard";
import Menu from "./common/Menu";
import Header from "./header/Header";
import { DEFAULT_RANGE_BOUNDS } from "./common/helpers";

function App() {
  const {currentUser, setCurrentUser, setCurrentOrg, setCurrentProject, modalState, requiresPayment, setRequiresPayment } = useAppContext();
  const [refreshData, setRefreshData] = useState(0);
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const navigate = useNavigate();
  const location = useLocation();

  moment.locale(language === 'nl' ? 'nl' : 'en');

  useEffect(() => {
    const user = AuthService.getCurrentUser();
    if (user) {
      setCurrentUser(user);
      const orgData = user.selectedOrg || { id: user.orgId, name: user.orgName, rangeBounds: user.orgRangeBounds, securityStandard: user.orgSecurityStandard };
      const projectData = ((user.role === 'superadmin' || user.role === 'admin') && !user.selectedProject) 
        ? { id: "", name: t('all_domains') }
        : user.selectedProject || { id: user.projectId, name: user.projectName };

      setCurrentOrg({ ...orgData, securityStandard: orgData.securityStandard || 'ISO27001', rangeBounds: orgData.rangeBounds?.length === 2 ? orgData.rangeBounds : DEFAULT_RANGE_BOUNDS });
      setCurrentProject(projectData);

      // Check if payment is required
      const now = new Date().getTime();
      const isMainEnvironment = user.environmentId === 'main';
      
      // Check if this is a trial environment (trialExpires > 0 means it was created as trial)
      const isTrialEnvironment = user.trialExpires && user.trialExpires > 0;
      
      // Check access status
      const hasActiveSubscription = user.subscriptionExpires && user.subscriptionExpires > now;
      const hasActiveTrial = user.trialExpires && user.trialExpires > now;

      // Require payment ONLY if:
      // - NOT in main environment AND
      // - Is a trial environment (trialExpires > 0) AND
      // - Has no active subscription AND no active trial
      if (!isMainEnvironment && isTrialEnvironment && !hasActiveSubscription && !hasActiveTrial) {
        setRequiresPayment(true);
      } else {
        setRequiresPayment(false);
      }

      // Show trial expiration notification if trial is still active
      if (hasActiveTrial && !hasActiveSubscription) {
        toast.info(t('Trial period expires {{time}}.', { time: moment(user.trialExpires).fromNow() }), { 
            position: "bottom-right", 
            autoClose: 10000, 
            closeOnClick: true, 
            transition: Bounce 
        });
      }

      // Show subscription expiration notification
      if (hasActiveSubscription && user.subscriptionExpires) {
        const daysUntilExpiration = Math.floor((user.subscriptionExpires - now) / (1000 * 60 * 60 * 24));
        if (daysUntilExpiration <= 30) {
          toast.warning(t('Subscription expires {{time}}.', { time: moment(user.subscriptionExpires).fromNow() }), { 
              position: "bottom-right", 
              autoClose: 10000, 
              closeOnClick: true, 
              transition: Bounce 
          });
        }
      }
    } else {
      setCurrentUser(null);
      setRequiresPayment(false);
    }
  }, [setCurrentUser, setCurrentOrg, setCurrentProject, setRequiresPayment]);

  // Redirect to payment page if payment is required
  useEffect(() => {
    if (currentUser && requiresPayment) {
      const paymentRoutes = ['/payment', '/payment-success', '/payment-cancelled'];
      if (!paymentRoutes.includes(location.pathname)) {
        navigate('/payment');
      }
    }
  }, [currentUser, requiresPayment, location.pathname, navigate]);

  if (currentUser === undefined) return "";

  const renderLoggedInContent = () => {
    const paymentRoutes = ['/payment', '/payment-success', '/payment-cancelled'];
    const isOnPaymentRoute = paymentRoutes.includes(location.pathname);
    
    // If payment is required OR on payment route, show simplified layout without Menu and Header
    if (requiresPayment || isOnPaymentRoute) {
      return (
        <>
          <ToastContainer/>
          <ModalManager modalState={modalState} setRefreshData={setRefreshData} />
          <LoggedInRoutes currentUser={currentUser} refreshData={refreshData} setRefreshData={setRefreshData} />
        </>
      );
    }

    // Normal logged-in layout with Menu and Header
    return (
      <>
        <Menu />
        <div id="main">
          <Header />
          <ToastContainer/>
          <ModalManager modalState={modalState} setRefreshData={setRefreshData} />
          <WelcomeWizard />
          <LoggedInRoutes currentUser={currentUser} refreshData={refreshData} setRefreshData={setRefreshData} />
        </div>
      </>
    );
  };

  const renderLoggedOutContent = () => (
    <>
      {shouldShowParticles() && <ParticlesComponent />}
      <ModalManager modalState={modalState} setRefreshData={setRefreshData} />
      <LoggedOutRoutes />
    </>
  );

  const paymentRoutes = ['/payment', '/payment-success', '/payment-cancelled'];
  const isOnPaymentRoute = paymentRoutes.includes(location.pathname);
  const showMenuAndHeader = currentUser && !requiresPayment && !isOnPaymentRoute;

  return (
    <>
      {!showMenuAndHeader && <div id="risqui-logo" className="icon-risqui" />}
      <div id="clouds" />
      {currentUser ? renderLoggedInContent() : renderLoggedOutContent()}
    </>
  );
}

export default App;
